import { useState } from 'react';
import { RESCUE_STATUS_LABEL, type RescueStatus } from '@coachrex/shared';
import { useSetRescueStatus, useUndoRescue } from '../lib/api';
import { Modal } from './Modal';
import { ErrorBox, Pills } from './ui';
import { useToast } from './Toast';

const PLACEHOLDER: Record<RescueStatus, string> = {
  open: 'Why does this need attention again?',
  completed: 'What did the rep do? e.g. Called back and left a voicemail.',
  recovered: 'What happened? e.g. Booked a site visit for Tuesday.',
  lost: 'Why was it lost? e.g. Went with another builder.',
  not_needed: 'Why isn’t a rescue needed? e.g. Already booked by phone.',
};

/** Manager override: pick a status (current one excluded), required note. Posts a note to the CRM, with Undo. */
export function StatusModal({ item, onClose }: { item: { id: string; leadName: string; status: RescueStatus }; onClose: () => void }) {
  const set = useSetRescueStatus();
  const undo = useUndoRescue();
  const toast = useToast();
  const options = (Object.keys(RESCUE_STATUS_LABEL) as RescueStatus[]).filter((s) => s !== item.status);
  const [status, setStatus] = useState<RescueStatus>(options[0]!);
  const [note, setNote] = useState('');
  const canSave = note.trim().length > 0 && !set.isPending;

  const save = () => {
    if (!canSave) return;
    set.mutate(
      { id: item.id, status, note: note.trim() },
      {
        onSuccess: ({ undoId }) => {
          onClose();
          toast({ text: `${item.leadName} moved to ${RESCUE_STATUS_LABEL[status]}`, undo: () => undo.mutateAsync(undoId) });
        },
      },
    );
  };

  return (
    <Modal title="Update status" subtitle={`${item.leadName} · currently ${RESCUE_STATUS_LABEL[item.status]}. The note is also added to the contact in your CRM.`} onClose={onClose} width={500}>
      <div className="form-label">
        <span>New status</span>
        <Pills options={options.map((s) => ({ value: s, label: RESCUE_STATUS_LABEL[s] }))} value={status} onChange={setStatus} />
      </div>
      <label className="form-label">
        <span>Note</span>
        <textarea className="text-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={PLACEHOLDER[status]} autoFocus />
      </label>
      {set.error && <ErrorBox error={set.error} />}
      <div className="modal__actions">
        <button className="btn btn--secondary" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn--accent" disabled={!canSave} onClick={save}>
          {set.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}
